import { useState, useEffect, useCallback } from 'react';
import type { ColumnConfig } from '@/components/configure-columns-dialog';

interface UseColumnConfigOptions {
  storageKey: string;
  defaultColumns: ColumnConfig[];
}

// Migration map for renamed column IDs
// When a column ID is renamed, add the old -> new mapping here
const COLUMN_ID_MIGRATIONS: Record<string, string> = {
  'assetId': 'code', // Renamed assetId to code for clarity (human-readable asset code)
};

export function useColumnConfig({ storageKey, defaultColumns }: UseColumnConfigOptions) {
  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    // Try to load from localStorage
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as ColumnConfig[];
        
        // Migrate old column IDs to new ones
        const migratedParsed = parsed.map((col) => {
          const newId = COLUMN_ID_MIGRATIONS[col.id];
          if (newId) {
            return { ...col, id: newId };
          }
          return col;
        });
        
        // Merge with defaults to handle new columns
        const mergedColumns = defaultColumns.map((defaultCol) => {
          const storedCol = migratedParsed.find((col) => col.id === defaultCol.id);
          return storedCol || defaultCol;
        });
        // Sort by order
        return mergedColumns.sort((a, b) => a.order - b.order);
      }
    } catch (error) {
      console.error('Failed to load column config from localStorage:', error);
    }
    return defaultColumns;
  });

  // Save to localStorage whenever columns change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(columns));
    } catch (error) {
      console.error('Failed to save column config to localStorage:', error);
    }
  }, [columns, storageKey]);

  const updateColumns = useCallback((newColumns: ColumnConfig[]): any => {
    setColumns(newColumns);
  }, []);

  const resetColumns = useCallback((): any => {
    setColumns(defaultColumns);
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Failed to remove column config from localStorage:', error);
    }
  }, [defaultColumns, storageKey]);

  // Get visibility map for easy lookup
  const columnVisibility = columns.reduce((acc, col) => {
    acc[col.id] = col.visible;
    return acc;
  }, {} as Record<string, boolean>);

  // Get ordered visible columns
  const visibleColumns = columns.filter((col) => col.visible).sort((a, b) => a.order - b.order);

  return {
    columns,
    columnVisibility,
    visibleColumns,
    updateColumns,
    resetColumns,
  };
}
